import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bell, CalendarDays, CheckCircle2, FileText, HardHat, Inbox, MapPin,
  Phone, PoundSterling, TrendingUp, Users, Wrench,
} from "lucide-react";
import { usePortalAuth } from "@/components/portal/PortalAuth";
import { Card, Spinner, StatusBadge, fmtDay, gbp, gbp2 } from "@/components/portal/shared";
import { QuickActions } from "@/components/portal/QuickActions";

const ACTIVITY_COLOURS = {
  lead: "bg-sky-500", note: "bg-slate-400", quote: "bg-amber-500",
  order: "bg-navy-900", payment: "bg-emerald-600", install: "bg-teal-600",
};

export default function DashboardPage() {
  const { api } = usePortalAuth();
  const [d, setD] = useState(null);

  const load = () => api.get("/dashboard").then((r) => setD(r.data)).catch(() => setD(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  if (d === null) return <Spinner />;
  if (d === false) return <p className="text-sm text-red-700" role="alert">Could not load the dashboard. Please refresh.</p>;

  const s = d.stats;
  const kpis = [
    ["Total enquiries", s.total, Users], ["New leads", s.new, Inbox],
    ["Visits today", s.visits_today, CalendarDays], ["Quote value (month)", gbp(s.quote_value_month), FileText],
    ["Revenue (month)", gbp(s.revenue_month), PoundSterling], ["Conversion", `${s.conversion}%`, TrendingUp],
    ["Follow-ups due", s.followups_due, Bell], ["Quotes accepted", s.quotes_accepted, CheckCircle2],
    ["Awaiting install", s.awaiting_install, HardHat], ["Deposits outstanding", gbp(s.deposits_outstanding), PoundSterling],
    ["Balances outstanding", gbp(s.balances_outstanding), PoundSterling], ["Completed jobs", s.completed, CheckCircle2],
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-7">
      <QuickActions onDone={load} />

      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
        {kpis.map(([label, value, Icon]) => (
          <div key={label} className="rounded-2xl border border-warm-200 bg-white p-4 shadow-sm" data-testid={`kpi-card-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 leading-tight">{label}</p>
              <Icon className="h-3.5 w-3.5 text-warm-300 shrink-0" />
            </div>
            <p className="font-serif text-2xl text-navy-900 leading-none">{value}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card title="Recent Leads" testid="dash-recent-leads" action={<Link to="/portal/leads" className="text-xs font-semibold text-navy-900 hover:underline">View all</Link>}>
          {d.recent_leads.length === 0 ? <p className="text-sm text-slate-400">No leads yet.</p> : (
            <ul className="divide-y divide-warm-200 -my-1">
              {d.recent_leads.map((l) => (
                <li key={l.id}>
                  <Link to={`/portal/customers/${l.id}`} data-testid={`dash-lead-${l.id}`} className="flex items-center gap-3 py-2.5 hover:bg-warm-100/50 -mx-2 px-2 rounded-lg transition-colors">
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-slate-900 truncate">{l.firstName} {l.lastName}</span>
                      <span className="block text-xs text-slate-500 truncate">{l.interest || "General enquiry"} · {l.postcode || "—"}</span>
                    </span>
                    <span className="ml-auto shrink-0"><StatusBadge s={l.status} simple /></span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Upcoming Home Visits" testid="dash-upcoming-visits" action={<Link to="/portal/schedule" className="text-xs font-semibold text-navy-900 hover:underline">View all</Link>}>
          {d.upcoming_visits.length === 0 ? <p className="text-sm text-slate-400">No visits booked.</p> : (
            <ul className="divide-y divide-warm-200 -my-1">
              {d.upcoming_visits.map((l) => (
                <li key={l.id} className="flex items-center gap-3 py-2.5 hover:bg-warm-100/50 -mx-2 px-2 rounded-lg transition-colors">
                  <Link to={`/portal/customers/${l.id}?tab=visits`} data-testid={`dash-visit-${l.id}`} className="flex items-center gap-3 min-w-0 flex-1">
                    <span className="rounded-lg bg-navy-900 text-white px-2.5 py-1.5 text-xs font-semibold text-center min-w-16 shrink-0">
                      {fmtDay(l.appointment.date)}{l.appointment.time ? <span className="block text-[10px] font-normal">{l.appointment.time}</span> : null}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-slate-900 truncate">{l.firstName} {l.lastName}</span>
                      <span className="block text-xs text-slate-500 truncate">{[l.address1, l.town, l.postcode].filter(Boolean).join(", ") || "—"}</span>
                    </span>
                  </Link>
                  {l.phone && <a href={`tel:${l.phone}`} aria-label={`Call ${l.firstName}`} className="ml-auto shrink-0 rounded-full border border-warm-300 p-2 text-navy-900 hover:bg-warm-100"><Phone className="h-3.5 w-3.5" /></a>}
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Recent Quotes" testid="dash-recent-quotes" action={<Link to="/portal/quotes" className="text-xs font-semibold text-navy-900 hover:underline">View all</Link>}>
          {d.recent_quotes.length === 0 ? <p className="text-sm text-slate-400">No quotes yet.</p> : (
            <ul className="divide-y divide-warm-200 -my-1">
              {d.recent_quotes.map((q) => (
                <li key={q.id}>
                  <Link to={`/portal/customers/${q.lead_id}?tab=quotes`} data-testid={`dash-quote-${q.id}`} className="flex items-center gap-3 py-2.5 hover:bg-warm-100/50 -mx-2 px-2 rounded-lg transition-colors">
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-slate-900">{q.ref} <span className="font-normal text-slate-500">· {q.customer}</span></span>
                      <span className="block text-xs text-slate-500">{fmtDay(q.created_at)}</span>
                    </span>
                    <span className="ml-auto text-sm font-semibold text-navy-900 shrink-0">{gbp(q.total)}</span>
                    <span className="shrink-0"><StatusBadge s={q.status} /></span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Upcoming Installations" testid="dash-upcoming-installs" action={<Link to="/portal/schedule" className="text-xs font-semibold text-navy-900 hover:underline">View all</Link>}>
          {d.upcoming_installations.length === 0 ? <p className="text-sm text-slate-400">No installations scheduled.</p> : (
            <ul className="divide-y divide-warm-200 -my-1">
              {d.upcoming_installations.map((o) => (
                <li key={o.id}>
                  <Link to={`/portal/customers/${o.lead_id}?tab=installations`} data-testid={`dash-install-${o.id}`} className="flex items-center gap-3 py-2.5 hover:bg-warm-100/50 -mx-2 px-2 rounded-lg transition-colors">
                    <Wrench className="h-4 w-4 text-warm-300 shrink-0" />
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-slate-900">{o.ref} <span className="font-normal text-slate-500">· {o.customer}</span></span>
                      <span className="block text-xs text-slate-500">{fmtDay(o.install_date)}{o.install_time ? ` · ${o.install_time}` : ""}{o.installer ? ` · ${o.installer}` : ""}</span>
                    </span>
                    <span className="ml-auto shrink-0"><StatusBadge s={o.status} /></span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Outstanding Payments" testid="dash-outstanding" action={<Link to="/portal/payments" className="text-xs font-semibold text-navy-900 hover:underline">View all</Link>}>
          {d.outstanding_payments.length === 0 ? <p className="text-sm text-slate-400">Nothing outstanding.</p> : (
            <ul className="divide-y divide-warm-200 -my-1">
              {d.outstanding_payments.map((o) => {
                const due = Math.max(0, (o.deposit_required || 0) - (o.deposit_paid || 0)) + Math.max(0, (o.balance_due || 0) - (o.balance_paid || 0));
                return (
                  <li key={o.id}>
                    <Link to={`/portal/customers/${o.lead_id}?tab=payments`} data-testid={`dash-payment-${o.id}`} className="flex items-center gap-3 py-2.5 hover:bg-warm-100/50 -mx-2 px-2 rounded-lg transition-colors">
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-slate-900">{o.ref} <span className="font-normal text-slate-500">· {o.customer}</span></span>
                        <span className="block text-xs text-slate-500">{(o.deposit_required || 0) - (o.deposit_paid || 0) > 0 ? "Deposit due" : "Balance due"}</span>
                      </span>
                      <span className="ml-auto text-sm font-semibold text-amber-700 shrink-0">{gbp2(due)}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <Card title="Recent Activity" testid="dash-activity">
          {d.recent_activity.length === 0 ? <p className="text-sm text-slate-400">No activity yet.</p> : (
            <ul className="space-y-3">
              {d.recent_activity.map((a, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${ACTIVITY_COLOURS[a.kind] || "bg-slate-300"}`} />
                  <div className="min-w-0">
                    {a.lead_id ? (
                      <Link to={`/portal/customers/${a.lead_id}`} className="text-sm text-slate-700 hover:text-navy-900">{a.text}</Link>
                    ) : (
                      <p className="text-sm text-slate-700">{a.text}</p>
                    )}
                    <p className="text-[11px] text-slate-400">{fmtDay(a.at)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {d.outstanding_payments.length === 0 && d.upcoming_installations.length === 0 && (
        <p className="text-[11px] text-slate-400 flex items-center gap-1.5"><MapPin className="h-3 w-3" /> Dashboard refreshes each time you open it.</p>
      )}
    </div>
  );
}
